import { useState, useMemo, useEffect } from 'react';
import {
  Modal,
  ComboBox,
  NumberInput,
  DatePicker,
  DatePickerInput,
} from '@carbon/react';
import { useAtom, useSetAtom } from 'jotai';
import searchAtomPair from 'atoms/search-atom';
import { debounce } from 'lodash-es';
import {
  EntityResponse,
  ReferenceLocationResponse,
  Establishment,
} from 'types/api-responses';
import { getCurrentDateString } from 'utils/dates';
import styles from './EstablishmentEditModal.module.scss';

interface EstablishmentEditModalProps {
  open: boolean;
  establishment: Partial<Establishment> | null;
  onSave: (establishment: Partial<Establishment>) => void;
  onClose: () => void;
}

export function EstablishmentEditModal({
  open,
  establishment,
  onSave,
  onClose,
}: EstablishmentEditModalProps) {
  const isNew = !establishment?.id;

  const [form, setForm] = useState<Partial<Establishment>>({});

  const {
    searchAtom: entitySearchAtom,
    resetSearchDataAtom: resetEntitySearch,
  } = useMemo(() => searchAtomPair<EntityResponse>('entities'), []);
  const {
    searchAtom: locationSearchAtom,
    resetSearchDataAtom: resetLocationSearch,
  } = useMemo(
    () => searchAtomPair<ReferenceLocationResponse>('referenceLocations'),
    []
  );

  const [entitySearchResults, searchEntities] = useAtom(entitySearchAtom);
  const [locationSearchResults, searchLocations] = useAtom(locationSearchAtom);
  const resetEntities = useSetAtom(resetEntitySearch);
  const resetLocations = useSetAtom(resetLocationSearch);

  const debouncedSearchEntities = useMemo(
    () => debounce(searchEntities, 400),
    [searchEntities]
  );
  const debouncedSearchLocations = useMemo(
    () => debounce(searchLocations, 400),
    [searchLocations]
  );

  // Initialize form when modal opens
  useEffect(() => {
    if (open) {
      setForm(
        establishment ?? {
          establishmentDate: getCurrentDateString(),
          surface: null,
          mineral: null,
        }
      );
      // Trigger initial searches
      searchEntities(() => ({
        text: establishment?.entityName || '',
        pageSize: 10,
      }));
      searchLocations(() => ({
        text: establishment?.referenceLocationName || '',
        pageSize: 10,
      }));
    }
  }, [open, establishment, searchEntities, searchLocations]);

  const handleClose = () => {
    resetEntities(undefined);
    resetLocations(undefined);
    onClose();
  };

  const handleSubmit = () => {
    onSave(form);
    resetEntities(undefined);
    resetLocations(undefined);
    onClose();
  };

  const entityItems = useMemo(() => {
    const results = entitySearchResults?.data;
    if (!results || Array.isArray(results)) return [];
    return (results.results || []).map((e: EntityResponse) => ({
      id: e.id,
      label: e.name || `Entity ${e.id}`,
    }));
  }, [entitySearchResults.data]);

  const locationItems = useMemo(() => {
    const results = locationSearchResults?.data;
    if (!results || Array.isArray(results)) return [];
    return (results.results || []).map((loc: ReferenceLocationResponse) => ({
      id: loc.id,
      label: loc.name || `Location ${loc.id}`,
    }));
  }, [locationSearchResults.data]);

  const canSave = form.referenceLocationId && form.entityId;

  return (
    <Modal
      open={open}
      modalHeading={isNew ? 'Add Establishment' : 'Edit Establishment'}
      primaryButtonText="Save"
      secondaryButtonText="Cancel"
      onRequestClose={handleClose}
      onRequestSubmit={handleSubmit}
      primaryButtonDisabled={!canSave}
      size="sm"
    >
      <div className={styles.formGrid}>
        <ComboBox
          id="est-modal-location"
          titleText="Reference Location"
          placeholder="Search locations..."
          items={locationItems}
          itemToString={(item: { label: string } | null) => item?.label ?? ''}
          selectedItem={
            form.referenceLocationId
              ? (locationItems.find(
                  (i) => i.id === form.referenceLocationId
                ) ?? {
                  id: form.referenceLocationId,
                  label: form.referenceLocationName || '',
                })
              : null
          }
          onInputChange={(text: string) => {
            debouncedSearchLocations(() => ({ text, pageSize: 10 }));
          }}
          onChange={({ selectedItem }) => {
            setForm((prev) => ({
              ...prev,
              referenceLocationId: selectedItem?.id,
              referenceLocationName: selectedItem?.label,
            }));
          }}
        />

        <ComboBox
          id="est-modal-entity"
          titleText="Entity"
          placeholder="Search entities..."
          items={entityItems}
          itemToString={(item: { label: string } | null) => item?.label ?? ''}
          selectedItem={
            form.entityId
              ? (entityItems.find((i) => i.id === form.entityId) ?? {
                  id: form.entityId,
                  label: form.entityName || '',
                })
              : null
          }
          onInputChange={(text: string) => {
            debouncedSearchEntities(() => ({ text, pageSize: 10 }));
          }}
          onChange={({ selectedItem }) => {
            setForm((prev) => ({
              ...prev,
              entityId: selectedItem?.id,
              entityName: selectedItem?.label,
            }));
          }}
        />

        <DatePicker
          datePickerType="single"
          dateFormat="m/d/Y"
          value={form.establishmentDate || ''}
          onChange={([date]: Date[]) => {
            if (date) {
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, '0');
              const day = String(date.getDate()).padStart(2, '0');
              setForm((prev) => ({
                ...prev,
                establishmentDate: `${year}-${month}-${day}`,
              }));
            }
          }}
        >
          <DatePickerInput
            id="est-modal-date"
            labelText="Establishment Date"
            placeholder="mm/dd/yyyy"
          />
        </DatePicker>

        <div className={styles.percentageRow}>
          <NumberInput
            id="est-modal-surface"
            label="Surface (%)"
            size="md"
            min={0}
            max={100}
            step={0.1}
            value={
              form.surface !== null && form.surface !== undefined
                ? form.surface * 100
                : ''
            }
            onChange={(_e: unknown, { value }: { value: string | number }) => {
              const num = typeof value === 'string' ? parseFloat(value) : value;
              setForm((prev) => ({
                ...prev,
                surface: isNaN(num) ? null : num / 100,
              }));
            }}
            allowEmpty
          />
          <NumberInput
            id="est-modal-mineral"
            label="Mineral (%)"
            size="md"
            min={0}
            max={100}
            step={0.1}
            value={
              form.mineral !== null && form.mineral !== undefined
                ? form.mineral * 100
                : ''
            }
            onChange={(_e: unknown, { value }: { value: string | number }) => {
              const num = typeof value === 'string' ? parseFloat(value) : value;
              setForm((prev) => ({
                ...prev,
                mineral: isNaN(num) ? null : num / 100,
              }));
            }}
            allowEmpty
          />
        </div>
      </div>
    </Modal>
  );
}
